import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import CommonModal from "./CommonModal";
import Style from "./modal.module.scss";
import {
  buttonName,
  ppRequiredModalData,
  commonErrMsg,
  responseStatus
} from "../../locales/en";
import { FaRegTimesCircle } from "react-icons/fa";
import privateAxios from "../../utils/axiosPrivate";
import { getUploadPPRequiredURL } from "../../utils/constants/apiEndPoints";
import { showToast } from "../../utils/utils";
import {
  DISALLOWED_FILE_EXTENSIONS,
  POP
} from "../../utils/constants/appConstants";

const PpRequiredModal = ({
  isOpen,
  toggle,
  quoteIds,
  onRequestCreditLimit
}) => {
  const fileInput = useRef(null);
  const [files, setFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);

  const handleUploadButtonClick = () => {
    if (fileInput.current) {
      fileInput.current.click();
    }
  };

  const handleCloseButton = () => {
    toggle();
    setFiles([]);
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    const validFiles = [];

    selectedFiles.forEach((file) => {
      const extension = file.name.split(".").pop().toLowerCase();
      if (DISALLOWED_FILE_EXTENSIONS.includes(extension)) {
        showToast(
          `"${file.name}" ${ppRequiredModalData?.invalidFileType}`,
          responseStatus?.error
        );
        return;
      }
      validFiles.push(file);
    });

    setFiles((prev) => [...prev, ...validFiles]);
    e.target.value = null;
  };

  const handleCreditLimitClick = () => {
    setFiles([]);
    onRequestCreditLimit();
  };

  const handleRemoveFile = (index) => {
    const updated = [...files];
    updated.splice(index, 1);
    setFiles(updated);
    if (fileInput.current) {
      fileInput.current.value = null;
    }
  };

  const handlePOPFileUpload = async () => {
    if (files.length === 0) {
      showToast(ppRequiredModalData?.selectFileError, responseStatus?.error);
      return;
    }

    try {
      setIsUploading(true);
      const formDataToSend = new FormData();
      formDataToSend.append(
        "request",
        new Blob([JSON.stringify({ quoteIds })], { type: "application/json" })
      );
      files.forEach((file) => formDataToSend.append("files", file));

      const response = await privateAxios.post(
        getUploadPPRequiredURL,
        formDataToSend,
        {
          params: { documentType: POP },
          headers: { "Content-Type": "multipart/form-data" }
        }
      );

      showToast(
        response?.data?.message || ppRequiredModalData?.uploadSuccess,
        responseStatus?.success
      );
      handleCloseButton();
    } catch (error) {
      const errorMessage = error.response?.data?.message || commonErrMsg?.item4;
      showToast(errorMessage, responseStatus?.error);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <CommonModal
      isOpen={isOpen}
      toggle={() => {}}
      title={ppRequiredModalData?.title}
      modalClassName={`modal-lg`}
      headerClassName={`modal-header-close hide-close-btn`}
      bodyClassName={Style.commentModalWrap}
      hideFooter
    >
      <div className="row">
        <div className="mb-3">
          <p>{ppRequiredModalData?.content1}</p>
          <p>{ppRequiredModalData?.content2}</p>
        </div>
        <div className="mb-3">
          <div className={Style.modalInputwrap}>
            <label htmlFor="" className="form-label">
              {ppRequiredModalData?.uploadField}
            </label>
            <div className={Style.formInput}>
              <div className="upload-file">
                <button
                  type="button"
                  onClick={handleUploadButtonClick}
                  className={`btn border-btn-primary fundtap_agreement pp-upload-btn spinDiv d-block`}
                >
                  {buttonName?.upload}
                </button>
                <input
                  name="isFile"
                  type="file"
                  ref={fileInput}
                  multiple
                  onChange={handleFileChange}
                  className="d-none"
                />
              </div>
            </div>
          </div>
        </div>
        {files.length > 0 && (
          <div className={`mb-3 ${Style.fileList}`}>
            {files.map((file, index) => (
              <div key={index} className={Style.fileItem}>
                <span>{file.name}</span>
                <button
                  type="button"
                  className="files-remove-btn ms-1"
                  onClick={() => handleRemoveFile(index)}
                >
                  <FaRegTimesCircle />
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="mb-2">
          <div className={Style.modalInputwrap}>
            <button
              type="button"
              className="btn btn-primary me-2 mb-1"
              onClick={handlePOPFileUpload}
              disabled={isUploading}
            >
              {buttonName?.submit}
            </button>
            <button
              type="button"
              className="btn btn-primary mb-1"
              onClick={handleCreditLimitClick}
            >
              {ppRequiredModalData?.creditLimit}
            </button>
          </div>
        </div>
      </div>
    </CommonModal>
  );
};

PpRequiredModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  quoteIds: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.number, PropTypes.string])
  ),
  onRequestCreditLimit: PropTypes.func.isRequired
};

PpRequiredModal.defaultProps = {
  quoteIds: []
};

export default PpRequiredModal;
